/**
 * Stage 2.5 — Claim 1 category confirmation panel.
 *
 * Sits above the StrategyCardsTabPanel inside Stage 2's strategy
 * tab. Surfaces the LLM-suggested Claim 1 category from
 * ``stage_2_5.content.suggested_category`` (populated by the
 * background classifier kicked off at Stage 2 freeze) and lets the
 * drafter override or confirm. Until ``selected_category`` is non-
 * null, Stage 3 fails closed with ``error_code=category_not_selected``.
 *
 * The seven categories mirror the backend enum
 * (``apparatus / system / method / composition / use / kit / crm``).
 * Pure UI — calls ``suggestClaimCategory`` to refresh the LLM pick
 * on demand and ``confirmClaimCategory`` to lock the drafter's
 * choice.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, CheckCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import * as api from '@/api/client';
import type { ClaimCategory } from '@/api/client';
import { CLAIM_CATEGORIES } from '@/api/client';

import { apiErrorText } from '../../../lib/describeApiError';
const CATEGORY_RATIONALE: Record<ClaimCategory, string> = {
  apparatus:   'Physical hardware, mechanical or optical device.',
  system:      'Multiple interacting components forming an architecture.',
  method:      'Sequence of steps; use only when the contribution is procedural.',
  composition: 'Chemical, biological, or material composition.',
  use:         'Second medical use / new use of a known compound.',
  kit:         'Combination of articles packaged together.',
  crm:         'Non-transitory computer-readable medium with instructions.',
};

interface Props {
  matterId: string;
  /** Read from ``matterState.stages.stage_2_5.content`` so the panel
   *  hydrates on mount with whatever the background classifier
   *  already wrote. */
  content: {
    suggested_category?: ClaimCategory | null;
    selected_category?: ClaimCategory | null;
  };
  /** Called after a successful confirm so the parent can refresh
   *  the matter state and unlock the rest of the strategy tab. */
  onConfirmed: () => Promise<void> | void;
}

export default function CategoryConfirmPanel({
  matterId, content, onConfirmed,
}: Props) {
  const initialChoice: ClaimCategory =
    content.selected_category
    || content.suggested_category
    || 'apparatus';
  const [choice, setChoice] = useState<ClaimCategory>(initialChoice);
  const [suggested, setSuggested] = useState<ClaimCategory | null>(
    content.suggested_category || null,
  );
  const [confirmed, setConfirmed] = useState<ClaimCategory | null>(
    content.selected_category || null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-fetch the suggestion when neither selection nor suggestion
  // is in the matter state. Stage 2 freeze normally kicks off the
  // background classifier so this fast path rarely fires; it's
  // mainly the recovery for legacy matters and dev-rebuilds where
  // the freeze trigger never ran.
  useEffect(() => {
    if (suggested || confirmed) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const r = await api.suggestClaimCategory(matterId);
        if (cancelled) return;
        setSuggested(r.suggested_category);
        if (!confirmed) setChoice(r.suggested_category);
      } catch (e: any) {
        if (cancelled) return;
        setError(apiErrorText(e, 'Could not load suggestion'));
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [matterId, suggested, confirmed]);

  const refreshSuggestion = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.suggestClaimCategory(matterId);
      setSuggested(r.suggested_category);
      // Don't yank a confirmed pick out from under the drafter; only
      // re-seed ``choice`` when no selection is in flight.
      if (!confirmed) setChoice(r.suggested_category);
    } catch (e: any) {
      setError(apiErrorText(e, 'Re-classify failed'));
    } finally {
      setBusy(false);
    }
  }, [matterId, confirmed]);

  const handleConfirm = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await api.confirmClaimCategory(matterId, choice);
      setConfirmed(choice);
      await onConfirmed();
    } catch (e: any) {
      setError(apiErrorText(e, 'Confirm failed'));
    } finally {
      setBusy(false);
    }
  }, [matterId, choice, onConfirmed]);

  return (
    <div
      className="mb-6 rounded-sm border border-zinc-200 bg-white p-4"
      data-testid="category-confirm-panel"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900">
          Claim 1 category
        </h3>
        {confirmed && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200"
            data-testid="category-confirmed-pill"
          >
            <CheckCircle size={12} /> locked: {confirmed}
          </span>
        )}
      </div>

      <p className="mb-3 text-xs text-zinc-500">
        Pick the category Claim 1 should take in {' '}
        the generated set. Stage 3 cannot run until this is confirmed.
      </p>

      {error && (
        <div
          className="mb-3 flex items-center gap-2 rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
          data-testid="category-error"
        >
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-2" data-testid="category-options">
        {CLAIM_CATEGORIES.map((cat) => {
          const isSelected = choice === cat;
          const isSuggested = suggested === cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setChoice(cat)}
              className={[
                'rounded-sm border px-3 py-1.5 text-xs font-medium transition-colors',
                isSelected
                  ? 'border-[#002FA7] bg-[#002FA7] text-white'
                  : 'border-zinc-300 bg-white text-zinc-700 hover:border-[#002FA7] hover:text-[#002FA7]',
              ].join(' ')}
              data-testid={`category-option-${cat}`}
              aria-pressed={isSelected}
            >
              {cat}
              {isSuggested && !isSelected && (
                <span className="ml-1 text-[10px] text-zinc-500">
                  (suggested)
                </span>
              )}
            </button>
          );
        })}
      </div>

      <p className="mb-4 text-xs text-zinc-600" data-testid="category-rationale">
        {CATEGORY_RATIONALE[choice]}
      </p>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleConfirm}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-sm bg-[#002FA7] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0026A0] disabled:opacity-50"
          data-testid="confirm-category-button"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : null}
          {confirmed === choice
            ? 'Re-confirm'
            : confirmed
              ? `Change to ${choice}`
              : `Confirm ${choice}`}
        </button>
        <button
          type="button"
          onClick={refreshSuggestion}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-sm border border-zinc-300 bg-white px-2 py-1.5 text-xs text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
          title="Re-run classifier"
          data-testid="refresh-suggestion-button"
        >
          <RefreshCw size={12} /> Re-classify
        </button>
      </div>
    </div>
  );
}
